import React from 'react';
import { CheckCircle2, PlayCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import StepCard from 'shared/components/StepCard';
import type { SupportedLanguage } from 'shared/i18n/i18n';
import type { TrainingStageView } from 'features/courier-training/services/courierTrainingService';
import StageQuiz from 'features/courier-training/components/StageQuiz';
import VideoPlayer from 'features/courier-training/components/VideoPlayer';

interface TrainingStagePanelProps {
  stage: TrainingStageView;
  stageNumber: number;
  activeVideoIndex: number;
  language: SupportedLanguage;
  savingVideoId: string | null;
  videoSaveFailed: boolean;
  submittingQuiz: boolean;
  onVideoSelect: (index: number) => void;
  onVideoEnded: (videoId: string, alreadyCompleted: boolean) => void;
  onQuizSubmit: (answers: Record<string, number>) => void;
}

const TrainingStagePanel: React.FC<TrainingStagePanelProps> = ({
  stage,
  stageNumber,
  activeVideoIndex,
  language,
  savingVideoId,
  videoSaveFailed,
  submittingQuiz,
  onVideoSelect,
  onVideoEnded,
  onQuizSubmit,
}) => {
  const { t } = useTranslation();
  const activeVideo = stage.videos[activeVideoIndex] ?? stage.videos[0];
  const firstIncompleteIndex = stage.videos.findIndex(
    (video) => !video.completed,
  );

  if (!activeVideo) return null;

  return (
    <StepCard
      title={String(t(stage.titleKey))}
      headerSlot={
        <header className="mb-5 flex items-start justify-between gap-3 sm:mb-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-[#bf0035]">
              {t('portal.training.stageNumber', { number: stageNumber })}
            </p>
            <h2 className="mt-1 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
              {t(stage.titleKey)}
            </h2>
          </div>
          {stage.done ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
              <CheckCircle2 size={14} />
              {t('portal.training.completed')}
            </span>
          ) : null}
        </header>
      }
    >
      {stage.videos.length > 1 ? (
        <div
          className="mb-4 grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${stage.videos.length}, minmax(0, 1fr))`,
          }}
        >
          {stage.videos.map((video, index) => {
            const isActive = index === activeVideoIndex;
            const isAvailable =
              video.completed ||
              firstIncompleteIndex === -1 ||
              index <= firstIncompleteIndex;

            return (
              <button
                key={video.id}
                type="button"
                disabled={!isAvailable}
                onClick={() => onVideoSelect(index)}
                className={[
                  'flex min-w-0 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-semibold transition-colors sm:text-sm',
                  isActive
                    ? 'border-[#bf0035] bg-[#bf0035]/[0.06] text-[#bf0035]'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-[#bf0035]/30',
                  !isAvailable ? 'cursor-not-allowed opacity-40' : '',
                ].join(' ')}
              >
                {video.completed ? (
                  <CheckCircle2 size={15} className="shrink-0" />
                ) : (
                  <PlayCircle size={15} className="shrink-0" />
                )}
                <span className="truncate">{t(video.titleKey)}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-black">
        <VideoPlayer
          src={activeVideo.url}
          restrictSeek={!activeVideo.completed}
          onEnded={() =>
            onVideoEnded(activeVideo.id, activeVideo.completed)
          }
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 font-medium text-gray-600">
          {activeVideo.completed ? (
            <CheckCircle2 size={14} className="text-emerald-600" />
          ) : (
            <PlayCircle size={14} className="text-[#bf0035]" />
          )}
          {t(activeVideo.titleKey)}
        </span>
        {savingVideoId === activeVideo.id ? (
          <span className="text-gray-500">{t('portal.training.saving')}</span>
        ) : activeVideo.completed ? (
          <span className="font-semibold text-emerald-700">
            {t('portal.training.completed')}
          </span>
        ) : (
          <span className="text-gray-500">
            {t('portal.training.watchToComplete')}
          </span>
        )}
      </div>

      {videoSaveFailed ? (
        <div
          role="alert"
          className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {t('portal.training.videoSaveFailed')}
        </div>
      ) : null}

      {stage.hasQuiz && stage.quiz ? (
        stage.videosWatched ? (
          <div className="mt-5 overflow-hidden rounded-xl border border-gray-200">
            <StageQuiz
              key={`${stage.id}-${stage.quiz.answered}`}
              quiz={stage.quiz}
              language={language}
              disabled={false}
              submitting={submittingQuiz}
              onSubmit={onQuizSubmit}
            />
          </div>
        ) : (
          <div className="mt-4 rounded-xl bg-gray-50 p-4 text-sm text-gray-500">
            {t('portal.training.quiz.watchFirst')}
          </div>
        )
      ) : null}
    </StepCard>
  );
};

export default TrainingStagePanel;
